import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Check, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/app/components/ui/dialog'
import { Button } from '@/app/components/ui/button'
import { Input } from '@/app/components/ui/input'
import { Textarea } from '@/app/components/ui/textarea'
import { Doctor, posDoctorService } from '@/app/services/pos.service'

export interface NewDoctorModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (doctor: Doctor) => void
}

const emptyForm = {
  full_name: '',
  professional_license: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zip: '',
  country: 'MX',
}

type ApiValidationError = {
  response?: { data?: { errors?: Record<string, string[]> } }
}

export function NewDoctorModal({ open, onOpenChange, onCreated }: NewDoctorModalProps) {
  const [form, setForm] = useState(emptyForm)

  useEffect(() => {
    if (!open) setForm(emptyForm)
  }, [open])

  const createMutation = useMutation({
    mutationFn: posDoctorService.createDoctor,
    onSuccess: (doctor) => {
      toast.success('Médico registrado correctamente.')
      onCreated(doctor)
      onOpenChange(false)
    },
    onError: (error: unknown) => {
      const errors = (error as ApiValidationError).response?.data?.errors
      toast.error(errors ? Object.values(errors)[0]?.[0] : 'No se pudo registrar el médico.')
    },
  })

  const save = () => {
    if (Object.values(form).some((value) => !value.trim())) {
      toast.error('Complete todos los datos del médico.')
      return
    }
    createMutation.mutate(form)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Registrar nuevo médico</DialogTitle>
          <DialogDescription>El médico quedará disponible para todas las sucursales de la empresa.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Nombre completo">
            <Input value={form.full_name} onChange={(event) => setForm({ ...form, full_name: event.target.value })} placeholder="Dra. Laura Pérez García" />
          </Field>
          <Field label="Cédula profesional">
            <Input value={form.professional_license} onChange={(event) => setForm({ ...form, professional_license: event.target.value })} placeholder="1234567" />
          </Field>

          <Field label="Calle">
            <Textarea value={form.address_line1} onChange={(event) => setForm({ ...form, address_line1: event.target.value })} placeholder="Consultorio, calle y número" rows={2} />
          </Field>
          <Field label="Colonia">
            <Textarea value={form.address_line2} onChange={(event) => setForm({ ...form, address_line2: event.target.value })} placeholder="Consultorio, calle y número" rows={2} />
          </Field>
          <Field label="Ciudad">
            <Input value={form.city} onChange={(event) => setForm({ ...form, city: event.target.value })} placeholder="Consultorio, calle y número" rows={2} />
          </Field>
          <Field label="Estado">
            <Input value={form.state} onChange={(event) => setForm({ ...form, state: event.target.value })} placeholder="Consultorio, calle y número" rows={2} />
          </Field>
          <Field label="Código postal">
            <Input value={form.zip} onChange={(event) => setForm({ ...form, zip: event.target.value })} placeholder="Consultorio, calle y número" rows={2} />
          </Field>
          <div className="flex gap-2">
            <Button onClick={save} disabled={createMutation.isPending} className="flex-1">
              {createMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
              Guardar médico
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={createMutation.isPending}>Cancelar</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-slate-700">{label} <span className="text-red-600">*</span></label>
      {children}
    </div>
  )
}
